import React, { useState } from 'react';
import styled from 'styled-components';
import {
  ArrowDownIcon,
  BaseProps,
  DropdownMenu,
  DropdownMenuItem,
  SvgIcon,
  useMatchMedia,
} from '@make-software/cspr-design';

export interface NavDropdownProps extends BaseProps {
  title: React.ReactNode | string;
  itemList?: { handleClick?: () => void; component: React.ReactNode }[];
  opened?: boolean;
  stickToRight?: boolean;
  hideArrowButton?: boolean;
  spotlightTargetClassName?: string;
}

type NavDropdownInnerProps = NavDropdownProps & {
  isOpen: boolean;
  handleClick: () => void;
};

const RelativeContainer = styled.div(({ theme }) => ({
  position: 'relative',
  padding: 0,
  margin: 0,
}));

const NavDropdownButton = styled.button(() => ({
  display: 'flex',
  alignItems: 'center',
  padding: 0,
  margin: 0,
  border: 0,
  background: 'transparent',
  color: 'inherit',
  cursor: 'pointer',
}));

const NavDropdownContainer = styled.div<{
  stickTo?: 'left' | 'right';
}>(({ theme, stickTo = 'left' }) => ({
  paddingTop: '20px',
  position: 'absolute',
  left: 'auto',
  right: stickTo === 'left' ? 'auto' : 0,
  top: 6,
  zIndex: theme.zIndex.dropdown,
  //added to fix safari bug with z-index
  transform: 'translate3d(0, 0, 1rem)',
}));

const ArrowSvgIcon = styled(SvgIcon)<{ isOpen: boolean }>(
  ({ theme, isOpen }) => ({
    path: {
      fill: isOpen
        ? theme.styleguideColors.contentOnFill
        : theme.styleguideColors.contentTertiary,
    },
  })
);

const StyledDropdownMenuItem = styled(DropdownMenuItem)(({ theme }) => ({
  lineHeight: '24px',
}));

export const NavDropdownInner = React.forwardRef<
  HTMLButtonElement,
  NavDropdownInnerProps
>(
  (
    {
      title,
      itemList,
      stickToRight,
      hideArrowButton = false,
      isOpen,
      handleClick,
    }: NavDropdownInnerProps,
    ref
  ) => {
    const handleKeyDown = (ev) => {
      if (ev.key === 'Enter') {
        ev.preventDefault();
        handleClick();
      }
    };
    return (
      <>
        <NavDropdownButton
          ref={ref}
          onClick={(event) => {
            event.stopPropagation();
            handleClick();
          }}
          onKeyDown={handleKeyDown}
          aria-haspopup="menu"
          aria-expanded={isOpen}
        >
          {title}
          {!hideArrowButton && (
            <ArrowSvgIcon
              src={ArrowDownIcon}
              alt={'Arrow down icon'}
              role={'img'}
              style={{ marginLeft: 8 }}
              isOpen={isOpen}
              rotate={isOpen}
            />
          )}
        </NavDropdownButton>

        {isOpen && (
          <NavDropdownContainer stickTo={stickToRight ? 'right' : 'left'}>
            <DropdownMenu opened={isOpen} onClose={handleClick}>
              {itemList &&
                itemList
                  .filter(({ component }) => component != null)
                  .map(({ handleClick, component }, index) => (
                    <StyledDropdownMenuItem
                      key={index}
                      padding={'0px'}
                      onClick={handleClick && handleClick}
                    >
                      {component}
                    </StyledDropdownMenuItem>
                  ))}
            </DropdownMenu>
          </NavDropdownContainer>
        )}
      </>
    );
  }
);

export function NavDropdown(props: NavDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);

  const screenType = useMatchMedia(['mobile', 'mobile', 'desktop'], []);

  const handleMouseEnter = () => {
    setIsOpen(true);
  };

  const handleMouseLeave = () => {
    setIsOpen(false);
  };

  const handleClick = () => {
    setIsOpen((open) => !open);
  };

  return screenType === 'mobile' ? (
    <RelativeContainer>
      <NavDropdownInner isOpen={isOpen} {...props} handleClick={handleClick} />
    </RelativeContainer>
  ) : (
    <RelativeContainer
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      className={props.spotlightTargetClassName}
    >
      <NavDropdownInner isOpen={isOpen} {...props} handleClick={handleClick} />
    </RelativeContainer>
  );
}

export default NavDropdown;
